
"use client"

import { createContext, useContext, useEffect, useState } from "react"
import { useSession } from "@supabase/auth-helpers-react"

type Theme = "dark" | "light" | "system"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

type ThemeProviderState = {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
  theme: "light",
  setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "light",
  storageKey = "grace-awakens-theme",
  ...props
}: ThemeProviderProps) {
  const session = useSession()
  
  const [theme, setTheme] = useState<Theme>(() => {
    // If user is not signed in, always default to light mode
    if (!session) {
      return "light"
    }
    
    // If user is signed in, use stored preference or default
    return (localStorage.getItem(storageKey) as Theme) || defaultTheme
  })

  useEffect(() => {
    const root = window.document.documentElement
    
    root.classList.remove("light", "dark")
    
    // If no session, force light mode
    if (!session) {
      root.classList.add("light")
      return
    }
    
    if (theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light"
      
      root.classList.add(systemTheme)
      return
    }
    
    root.classList.add(theme)
  }, [theme, session])

  // Reset to light mode when user signs out
  useEffect(() => {
    if (!session) {
      setTheme("light")
    }
  }, [session])

  const value = {
    theme,
    setTheme: (theme: Theme) => {
      // Only allow theme changes for signed in users
      if (session) {
        localStorage.setItem(storageKey, theme)
        setTheme(theme)
      }
    },
  }

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)
  
  if (context === undefined)
    throw new Error("useTheme must be used within a ThemeProvider")
  
  return context
}
